import * as React from "react";
import { toast } from "react-toastify";
import { gql, useQuery } from "@apollo/client";
import "./StationDashboard.scss";
import Card from "react-bootstrap/Card";
import apiClient from "../../api";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Dropdown from "react-bootstrap/Dropdown";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faCopy,
  faEdit,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faArrowDown,
  faGripVertical,
  faNoteSticky,
} from "@fortawesome/free-solid-svg-icons";
import { gettext } from "i18n";
import ManualLabDataTable from "./ManualLabDataTable";
import {
  GetStationDashboardData,
  GetStationDashboardData_flow,
  GetStationDashboardDataVariables,
} from "./__generated__/GetStationDashboardData";
import {
  GetDatasourcesByUUID,
  GetDatasourcesByUUIDVariables,
} from "utils/__generated__/GetDatasourcesByUUID";
import { GetDatasourceDataByUUID_source } from "utils/__generated__/GetDatasourceDataByUUID";
import { GetDatasourceData_source_data } from "utils/__generated__/GetDatasourceData";
import {
  GetVerticalTemperature,
  GetVerticalTemperatureVariables,
  GetVerticalTemperature_get_vertical_temperature,
} from "utils/__generated__/GetVerticalTemperature";
import {
  GetMeasurementStationMetaByIdQuery,
  GetMeasurementStationMetaByIdQueryVariables,
} from "utils/__generated__/GetMeasurementStationMetaByIdQuery";
import { useCachedSubscription } from "../../hooks/apollo";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import { useModalContext } from "state/modal";
import { IntervalUnit } from "components/dates/DateTimeRangeSelector";
import {
  toLocaleDateString,
  formatNumberSafe,
  formatValueForTable,
  timestampToDatetimeString,
} from "utils/formatting";
import { insertIntoList } from "utils/arrays";
import { getLiteralArray } from "utils/hasura";
import {
  DataPoint,
  getChartSeriesColor,
  FullDataSourceLoader,
  isValidDataPointValue,
  AdditionalDatapointData,
  useCombinedSourceData,
} from "utils/datacharts";
// TODO: extract these components so they need not be imported from MeasurementStationPage
import { DataChart } from "pages/MeasurementStationPage";
import ChartAnnotationForm from "components/modals/ChartAnnotationForm";
import {
  getSourceUnitSymbol,
  GET_VERTICAL_TEMPERATURE,
  GET_MEASUREMENT_STATION_META_BY_ID,
  GET_DATASOURCES_BY_UUID,
} from "utils/stations";
import {
  useDraggable,
  useScopedDragEnd,
  useDragSelector,
  DragPayload,
  DropPosition,
} from "utils/dnd";
import { WidgetDropZone, RowDropZone } from "./DropZones";
import {
  HourlyDischargeTable,
  TotalDischargeTable,
} from "dashboards/widgets/DischargeTables";
import classNames from "classnames";
import {
  calculateDailyStats,
  formatAsDatasourceData,
  formatDailyAverages,
  formatDatasourceLabel,
  sortDailyAverageKeyList,
  VisualSettings,
  ChartVisualSettings,
} from "utils/widgets";
import {
  SIZE_PRESETS,
  DEFAULT_COL_SPAN,
  getSizePreset,
  calculateWidgetWidthDuringDrag,
  calculateWidgetDrop,
  resolveRowWidgets,
  useMediaQuery,
} from "utils/dashboards";
import CommentWidget from "./CommentWidget";
import LoadCalculationWidget from "../widgets/LoadCalculationWidget";

type AvailableWidget = {
  key: string;
  component: (
    sourceUUID: string | undefined,
    metaData: any,
    visualSettings?: VisualSettings,
    widgetContext?: { rowIndex: number; widgetIndex: number },
  ) => React.ReactNode;
};

interface RenderWidget extends AvailableWidget {
  sourceUUID?: string;
  metaData?: any;
  colSpan?: number;
  visualSettings?: VisualSettings;
}

type WidgetGroupItem = {
  key: string;
  sourceUUID?: string;
  metaData?: any;
  colSpan?: number;
  visualSettings?: VisualSettings;
};

type VerticalTemperatureRecord =
  GetVerticalTemperature_get_vertical_temperature;

const GET_STATION_DASHBOARD_DATA = gql`
  subscription GetStationDashboardData(
    $startDate: timestamptz!
    $endDate: timestamptz!
    $sourceType: String!
    $sourceId: Int!
  ) {
    flow: get_datasource(args: { type: $sourceType, id: $sourceId }) {
      type
      source_id
      source_uuid
      unit {
        id
        name
        symbol
      }
      name
      visible
      order
      widget_size
      station {
        id
        name
        identifier
      }
      datasource_annotation {
        id
        timestamp
        value
        text
        color
      }
      data(
        args: { start: $startDate, end: $endDate }
        order_by: { timestamp: asc }
      ) {
        timestamp
        value
        invalid
      }
    }
  }
`;

const getYAxisValue = (record: VerticalTemperatureRecord) => {
  let cm = record.system_name?.replace("vertical_temperature_", "") ?? "";
  cm = cm.replace("cm", "");

  const numericValue = parseInt(cm, 10);
  return numericValue !== 0 ? -numericValue : numericValue;
};

interface VerticalTempChartProps {
  stationDatasets: Record<number, StationDataset>;
}
const VerticalTempChart: React.FC<VerticalTempChartProps> = ({
  stationDatasets,
}) => {
  const [chartInstance, setChartInstance] = React.useState<any>(null);
  const [lastSeriesLength, setLastSeriesLength] = React.useState<number>(0);

  const stationNames = Object.values(stationDatasets).map(
    (set) => set.stationName,
  );

  // Sanitize temperature data to remove NaN values
  const sanitizedStationDatasets = React.useMemo(() => {
    const sanitized: Record<number, StationDataset> = {};

    Object.entries(stationDatasets).forEach(([key, dataset]) => {
      sanitized[parseInt(key)] = {
        ...dataset,
        temperatureData: dataset.temperatureData.filter((record) =>
          isValidDataPointValue(record.value),
        ),
      };
    });

    return sanitized;
  }, [stationDatasets]);

  let min: number = 0;
  let max: number = 0;
  let totalSeriesLength = 0;
  Object.values(sanitizedStationDatasets).forEach((dataset) => {
    const { temperatureData } = dataset;
    temperatureData.forEach((record) => {
      min = Math.min(min, record.value);
      max = Math.max(max, record.value);
      totalSeriesLength++;
    });
  });

  // Add some padding to the min and max values to prevent the chart from
  // having a value exactly at the edge of the chart.
  min -= 0.01;
  max += 0.01;
  if (min > -1) {
    min = -2;
  }
  if (max < 1) {
    max = 2;
  }

  const { series, valuesY } = React.useMemo(() => {
    let valuesY: any = [];
    const series = Object.values(sanitizedStationDatasets).map(
      (dataset, idx) => {
        const isFirst = idx === 0;
        const { stationName, temperatureData } = dataset;
        const seriesName = stationName;

        const valuesXY = temperatureData.map((record) => {
          const yAxisValue = getYAxisValue(record);
          valuesY.push(yAxisValue);
          return [record.value, yAxisValue];
        });
        // sort highest to lowest, otherwise it will draw nonsensical scribbles
        valuesXY.sort((a, b) => b[1] - a[1]);

        const seriesColor = getChartSeriesColor(idx);
        const lineStyle = {
          type: "solid",
          color: "rgba(162, 204, 255, 1)",
        };
        return {
          name: seriesName,
          type: "line",
          symbolSize: 6,
          symbol: "circle",
          smooth: true,
          itemStyle: {
            color: seriesColor,
            borderColor: seriesColor,
            borderWidth: 1,
          },
          lineStyle: {
            width: 3,
            color: seriesColor,
            shadowColor: "rgba(0,0,0,0.3)",
            shadowBlur: 10,
            shadowOffsetY: 8,
          },
          data: valuesXY,
          ...(isFirst && {
            markLine: {
              data: [{ name: "0 °C", xAxis: 0, lineStyle }],
              label: {
                formatter: () => {
                  return `0 °C`;
                },
                position: "middle",
                padding: [0, 0, -5, 0],
                show: true,
              },
              symbol: "none",
              silent: true,
            },
            markArea: {
              silent: true,
              data: [
                [
                  { originX: "start" },
                  {
                    xAxis: 0,
                    itemStyle: {
                      color: "rgba(182, 224, 255, 0.4)",
                    },
                  },
                ],
              ],
            },
          }),
        };
      },
    );

    // Sort valuesY from lowest to highest
    valuesY.sort((a: number, b: number) => a - b);
    return {
      series,
      valuesY,
    };
  }, [sanitizedStationDatasets]);

  const uniqueValuesY = Array.from(new Set(valuesY));
  const lowestValueY = uniqueValuesY[0];
  const highestValueY = uniqueValuesY[uniqueValuesY.length - 1];

  const chartOptions = React.useMemo<any>(() => {
    return {
      legend: {
        data: stationNames,
      },
      tooltip: {
        trigger: "axis",
        formatter: (value: any) => {
          const depth = value[0]?.data[1];
          let label = `${gettext("Temperature")} (${depth}cm):<br/>`;

          value.forEach((item: any) => {
            const temperature = item.data?.[0];
            label += `${item.marker} ${item.seriesName}: ${temperature}°C<br/>`;
          });

          return label;
        },
        axisPointer: {
          axis: "y",
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        axisLabel: {
          formatter: "{value} °C",
        },
        min: min.toFixed(2),
        max: max.toFixed(2),
      },
      yAxis: {
        type: "value",
        axisLine: { onZero: false },
        axisLabel: {
          formatter: "{value} cm",
        },
        boundaryGap: false,
        interval: 10,
        min: lowestValueY,
        max: highestValueY,
      },
      series,
    };
  }, [stationNames, min, max, series, lowestValueY, highestValueY]);

  React.useEffect(() => {
    if (!chartInstance) {
      return;
    }
    if (!chartOptions || !totalSeriesLength) {
      return;
    }

    const echartInstance = chartInstance.getEchartsInstance();
    if (lastSeriesLength !== totalSeriesLength) {
      echartInstance.clear();
      setLastSeriesLength(totalSeriesLength);
    }
    echartInstance.setOption(chartOptions);
  }, [chartOptions, chartInstance, lastSeriesLength, totalSeriesLength]);

  return (
    <ReactEChartsCore
      ref={setChartInstance}
      option={chartOptions}
      echarts={echarts}
      style={{ height: "18.75rem" }}
      opts={{
        locale: "FI",
      }}
    />
  );
};

const dailyValuesToDatapoints = (values: {
  [key: string]: number;
}): DataPoint[] => {
  return Object.keys(values).map((dateKey) => {
    const date = new Date(dateKey);

    // Date input (and chart startDate) is timezone-aware, but dateKey produces dates in UTC.
    // Align the dates by adjusting the timezone offset.
    const adjustedDate = new Date(
      date.getTime() - new Date().getTimezoneOffset() * 60 * 1000,
    );
    return {
      invalid: false,
      timestamp: adjustedDate.toISOString(),
      value: values[dateKey],
      __typename: "data",
    };
  });
};

interface StatsSummaryWidgetProps {
  flow: GetStationDashboardData_flow;
  datapoints: GetDatasourceData_source_data[];
  endDate: Date;
  ignoreZeroValues?: boolean;
}

const StatsSummaryWidget: React.FC<StatsSummaryWidgetProps> = ({
  flow,
  datapoints,
  endDate,
  ignoreZeroValues,
}) => {
  // Filter out invalid datapoints
  const validDatapoints =
    datapoints?.filter(
      (point) => !point.invalid && (!ignoreZeroValues || point.value !== 0),
    ) ?? [];

  const intervalStart = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
  const datapointsWithinInterval = validDatapoints.filter(
    (datapoint) => new Date(datapoint.timestamp) > intervalStart,
  );

  const lastDatapoint = validDatapoints[validDatapoints.length - 1];

  const averageWithinInterval =
    datapointsWithinInterval.reduce(
      (sum, datapoint) => sum + datapoint.value,
      0,
    ) / (datapointsWithinInterval.length || 1);

  const average30d =
    validDatapoints.reduce((sum, datapoint) => sum + datapoint.value, 0) /
    (validDatapoints.length || 1);

  const min30d = validDatapoints.reduce(
    (min, datapoint) => Math.min(min, datapoint.value),
    Infinity,
  );

  const max30d = validDatapoints.reduce(
    (max, datapoint) => Math.max(max, datapoint.value),
    -Infinity,
  );

  return (
    <Card>
      <Card.Body>
        <Table>
          <tbody>
            <tr>
              <th>
                Latest datapoint
                <br />
                <small>
                  (
                  {lastDatapoint
                    ? timestampToDatetimeString(lastDatapoint.timestamp)
                    : "N/A"}
                  )
                </small>
              </th>
              <td>
                <DashboardNumber
                  value={lastDatapoint?.value ?? 0}
                  unit={flow.unit?.symbol}
                />
              </td>
            </tr>
            <tr>
              <th>Change (24h)</th>
              <td>
                <DashboardNumber
                  value={averageWithinInterval}
                  unit={flow.unit?.symbol}
                />
              </td>
            </tr>
            <tr>
              <th>Average</th>
              <td>
                <DashboardNumber value={average30d} unit={flow.unit?.symbol} />
              </td>
            </tr>
            <tr>
              <th>Min</th>
              <td>
                <DashboardNumber
                  value={min30d !== Infinity ? min30d : 0}
                  unit={flow.unit?.symbol}
                />
              </td>
            </tr>
            <tr>
              <th>Max</th>
              <td>
                <DashboardNumber
                  value={max30d !== -Infinity ? max30d : 0}
                  unit={flow.unit?.symbol}
                />
              </td>
            </tr>
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

interface PrimaryParameterAverageChartProps {
  flow: GetStationDashboardData_flow;
  startDate: Date;
  endDate: Date;
  datapoints: GetDatasourceData_source_data[];
  ignoreZeroValues?: boolean;
}
const PrimaryParameterAverageChart: React.FC<
  PrimaryParameterAverageChartProps
> = ({ flow, startDate, endDate, datapoints, ignoreZeroValues }) => {
  const dailyStats = calculateDailyStats({
    datapoints,
    endDate,
    startDate,
    ignoreZeroValues,
  });
  const { dailyAverages, dailyMaxs, dailyMins } =
    formatDailyAverages(dailyStats);

  // Convert daily averages into a list of datapoints for a chart
  const dailyAverageDatapoints: DataPoint[] =
    dailyValuesToDatapoints(dailyAverages);
  const dailyMinDatapoints: GetDatasourceData_source_data[] =
    dailyValuesToDatapoints(dailyMins) as GetDatasourceData_source_data[];
  const dailyMaxDatapoints: GetDatasourceData_source_data[] =
    dailyValuesToDatapoints(dailyMaxs) as GetDatasourceData_source_data[];

  const additionalDatapoints: AdditionalDatapointData[] = [
    {
      source: flow as GetDatasourceDataByUUID_source,
      data: dailyMinDatapoints,
      seriesData: {
        name: "Min",
        stack: "confidence-band",
      },
    },
    {
      source: flow as GetDatasourceDataByUUID_source,
      data: dailyMaxDatapoints,
      subtractData: dailyMinDatapoints,
      seriesData: {
        name: "Max",
        stack: "confidence-band",
        areaStyle: {
          color: "#ccc",
        },
      },
    },
  ];

  return (
    <Card className="DataSourceCard">
      <Card.Header>
        <h5>
          {flow.name} [{flow.unit?.symbol}]: <span>Average</span>{" "}
        </h5>
      </Card.Header>
      <Card.Body>
        <DataChart
          confidenceBand
          source={flow}
          datapoints={dailyAverageDatapoints}
          additionalDatapoints={additionalDatapoints}
          startDate={startDate}
          endDate={endDate}
          datapointGroupDateFormat="YYYY-MM-DD"
          labelDateFormat="DD.MM.YYYY"
          ignoreZeroValues={ignoreZeroValues}
        />
      </Card.Body>
    </Card>
  );
};

interface DailyAveragesTableProps {
  startDate: Date;
  endDate: Date;
  datapoints: GetDatasourceData_source_data[];
  ignoreZeroValues?: boolean;
}
const DailyAveragesTable: React.FC<DailyAveragesTableProps> = ({
  startDate,
  endDate,
  datapoints,
  ignoreZeroValues,
}) => {
  const dailyStats = calculateDailyStats({
    datapoints,
    endDate,
    startDate,
    ignoreZeroValues,
  });

  const { dailyAverages, dailyAverageKeys, dailyMaxs, dailyMins } =
    formatDailyAverages(dailyStats);

  const copyToClipboard = () => {
    let tableContents = "Date\tMax\tAverage\tMin\n";
    dailyAverageKeys.forEach((dateKey) => {
      const average = dailyAverages[dateKey];
      const min = dailyMins[dateKey];
      const max = dailyMaxs[dateKey];
      const date = new Date(dateKey);
      tableContents += `${toLocaleDateString(date)}\t${formatValueForTable(
        max,
      )}\t${formatValueForTable(average)}\t${formatValueForTable(min)}\n`;
    });

    try {
      navigator.clipboard.writeText(tableContents);
      toast.success(gettext("Value copied to clipboard"));
    } catch (err) {
      console.error(
        "Failed to copy to clipboard: ",
        err,
        `\n\nValue:\n${tableContents}`,
      );
      toast.error(gettext("Action failed"));
    }
  };

  return (
    <Card className="DailyAveragesTable">
      <Card.Body>
        <Table>
          <thead>
            <tr>
              <th className="copy-to-clipboard" onClick={copyToClipboard}>
                <FontAwesomeIcon icon={faCopy} />
                <span>copy</span>
              </th>

              <th>Max</th>

              <th>Average</th>

              <th>Min</th>
            </tr>
          </thead>
          <tbody>
            {!dailyAverageKeys.length && (
              <tr>
                <td colSpan={4}>
                  <p>No data available</p>
                </td>
              </tr>
            )}
            {dailyAverageKeys.map((dateKey) => {
              const average = dailyAverages[dateKey];
              const min = dailyMins[dateKey];
              const max = dailyMaxs[dateKey];
              const date = new Date(dateKey);

              return (
                <tr key={dateKey}>
                  <td>{toLocaleDateString(date)}</td>

                  <td>{formatValueForTable(max)}</td>

                  <td>
                    <b>{formatValueForTable(average)}</b>
                  </td>

                  <td>{formatValueForTable(min)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

interface DailyAveragesTableMultiSourceProps {
  title?: string;
  startDate: Date;
  endDate: Date;
  sourceUUIDs: string[];
  ignoreZeroValues?: boolean;
}
const DailyAveragesTableMultiSource: React.FC<
  DailyAveragesTableMultiSourceProps
> = ({ title, startDate, endDate, sourceUUIDs, ignoreZeroValues }) => {
  const [combinedDatapoints, setSourceData] =
    useCombinedSourceData(sourceUUIDs, startDate, endDate);

  const processDatapoints = (datapoints: AdditionalDatapointData[]) =>
    datapoints.map((cp) => {
      const dailyStats = calculateDailyStats({
        datapoints: cp.data,
        startDate,
        endDate,
        ignoreZeroValues,
      });
      const formattedAverages = formatDailyAverages(dailyStats);

      return {
        data: formattedAverages,
        source: cp.source,
      };
    });

  const datasourceAverages = processDatapoints(combinedDatapoints);

  const combinedDailyAverageKeys = datasourceAverages.map(
    (cp) => cp.data.dailyAverageKeys,
  );
  const dailyAverageKeys = sortDailyAverageKeyList(
    Array.from(
      new Set(
        combinedDailyAverageKeys.reduce((acc, val) => acc.concat(val), []),
      ),
    ),
  );

  const copyToClipboard = () => {
    let tableContents = "Date\t";
    Object.entries(datasourceAverages).forEach(([key, value]) => {
      const headerName = `${value.source?.station?.name} - ${
        value.source.name
      }${value.source.unit?.symbol ? ` [${value.source.unit?.symbol}]` : ""}`;
      tableContents += `${headerName}\t`;
    });
    tableContents += "\n";

    dailyAverageKeys.forEach((dateKey) => {
      tableContents += `${dateKey}\t`;
      datasourceAverages.forEach((average) => {
        const dailyAverage = average.data.dailyAverages[dateKey] ?? 0;
        tableContents += `${formatValueForTable(dailyAverage)}\t`;
      });
      tableContents += "\n";
    });

    try {
      navigator.clipboard.writeText(tableContents);
      toast.success(gettext("Value copied to clipboard"));
    } catch (err) {
      console.error(
        "Failed to copy to clipboard: ",
        err,
        `\n\nValue:\n${tableContents}`,
      );
      toast.error(gettext("Action failed"));
    }
  };

  return (
    <Card className="DailyAveragesTableMultiSource">
      {!!title && (
        <Card.Header>
          <h5>{gettext(title)}</h5>
        </Card.Header>
      )}
      <Card.Body>
        {sourceUUIDs?.map((sourceUUID, idx) => (
          <FullDataSourceLoader
            key={`additional_loader_${sourceUUID}_${idx}`}
            sourceUUID={sourceUUID}
            startDate={startDate}
            endDate={endDate}
            additionalDatapoints={combinedDatapoints}
            setSourceData={setSourceData}
          />
        ))}
        <Table>
          <thead>
            <tr>
              <th className="copy-to-clipboard" onClick={copyToClipboard}>
                <FontAwesomeIcon icon={faCopy} />
                <span>copy</span>
              </th>

              {Object.entries(datasourceAverages).map(([key, value]) => (
                <th key={key}>
                  <span>{value.source?.station?.name}</span>
                  <br />
                  {value.source.name}{" "}
                  {value.source.unit?.symbol
                    ? `[${value.source.unit?.symbol}]`
                    : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!Object.keys(datasourceAverages).length && (
              <tr>
                <td colSpan={4}>
                  <p>No data available</p>
                </td>
              </tr>
            )}
            {dailyAverageKeys.map((dateKey) => {
              const date = new Date(dateKey);

              return (
                <tr key={dateKey}>
                  <td>{toLocaleDateString(date)}</td>

                  {Object.entries(datasourceAverages).map(([key, averages]) => {
                    const { data } = averages;
                    const average = data.dailyAverages[dateKey] ?? 0;
                    return (
                      <td key={`${key}_${dateKey}`}>
                        <b>{formatValueForTable(average)}</b>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

interface StationDataset {
  temperatureData: VerticalTemperatureRecord[];
  stationName: string;
}

interface TemperatureProfileWidgetMetadata {
  addStationIds: number[];
}

interface TemperatureProfileWidgetProps {
  stationId: number;
  startDate: Date;
  endDate: Date;
  metaData?: TemperatureProfileWidgetMetadata;
}

const TemperatureProfileWidget: React.FC<TemperatureProfileWidgetProps> = ({
  stationId,
  startDate,
  endDate,
  metaData,
}) => {
  const { addStationIds } = metaData ?? {};
  const [stationDatasets, setStationDatasets] = React.useState<
    Record<number, StationDataset>
  >({});
  const stationIds = React.useMemo(() => {
    if (!addStationIds?.length) {
      return [stationId];
    }
    return Array.from(new Set([...addStationIds]));
  }, [stationId, addStationIds]);
  const datasetLoaderMemo = React.useMemo(() => {
    return stationIds.map((stationId) => (
      <StationDatasetLoader
        key={`station_dataset_loader_${stationId}`}
        stationId={stationId}
        startDate={startDate}
        endDate={endDate}
        stationDatasets={stationDatasets}
        setStationDatasets={setStationDatasets}
      />
    ));
  }, [stationIds, startDate, endDate, stationDatasets, setStationDatasets]);

  return (
    <Card className={classNames("DataSourceCard")}>
      <Card.Header>
        <h5>Temperature profile</h5>
      </Card.Header>
      <Card.Body>
        {datasetLoaderMemo}
        {!Object.keys(stationDatasets).length && <Spinner animation="border" />}
        <VerticalTempChart stationDatasets={stationDatasets} />
      </Card.Body>
    </Card>
  );
};

interface StationDatasetLoaderProps {
  stationId: number;
  startDate: Date;
  endDate: Date;
  stationDatasets: Record<number, StationDataset>;
  setStationDatasets: (datasets: Record<number, StationDataset>) => void;
}

const StationDatasetLoader: React.FC<StationDatasetLoaderProps> = ({
  stationId,
  startDate,
  endDate,
  stationDatasets,
  setStationDatasets,
}) => {
  const { data: verticalTempData, loading: verticalTempLoading } = useQuery<
    GetVerticalTemperature,
    GetVerticalTemperatureVariables
  >(GET_VERTICAL_TEMPERATURE, {
    variables: {
      stationId: stationId,
      minTimestamp: startDate,
      maxTimestamp: endDate,
    },
  });

  // Get station name from GET_MEASUREMENT_STATION_META_BY_ID
  const { data: stationMeta, loading: stationMetaLoading } = useQuery<
    GetMeasurementStationMetaByIdQuery,
    GetMeasurementStationMetaByIdQueryVariables
  >(GET_MEASUREMENT_STATION_META_BY_ID, {
    variables: {
      id: stationId,
    },
  });

  React.useEffect(() => {
    if (!verticalTempData || verticalTempLoading) {
      return;
    }
    if (!stationMeta || stationMetaLoading) {
      return;
    }

    const data = verticalTempData?.get_vertical_temperature ?? [];
    const stationName = stationMeta?.station?.[0]?.name ?? "";

    setStationDatasets({
      ...stationDatasets,
      [stationId]: {
        stationName,
        temperatureData: data,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stationId,
    verticalTempData,
    verticalTempLoading,
    stationMeta,
    stationMetaLoading,
    setStationDatasets,
  ]);

  return <></>;
};

type LimitStatus = "green" | "yellow" | "red";

const getMinMaxValues = (
  datapoints: GetDatasourceData_source_data[],
): { min: number | null; max: number | null } => {
  let min: number | null = null;
  let max: number | null = null;
  datapoints.forEach((d) => {
    if (d.invalid || !isValidDataPointValue(d.value)) {
      return;
    }
    if (min === null || d.value < min) {
      min = d.value;
    }
    if (max === null || d.value > max) {
      max = d.value;
    }
  });
  return { min, max };
};

interface LimitBounds {
  warningLow?: number | null;
  criticalLow?: number | null;
  warningHigh?: number | null;
  criticalHigh?: number | null;
}

const getLimitStatus = (
  extremes: { min: number | null; max: number | null },
  limits: LimitBounds,
): LimitStatus => {
  const { min, max } = extremes;
  if (min === null && max === null) {
    return "green";
  }
  if (
    (limits.criticalLow != null && min != null && min <= limits.criticalLow) ||
    (limits.criticalHigh != null && max != null && max >= limits.criticalHigh)
  ) {
    return "red";
  }
  if (
    (limits.warningLow != null && min != null && min <= limits.warningLow) ||
    (limits.warningHigh != null && max != null && max >= limits.warningHigh)
  ) {
    return "yellow";
  }
  return "green";
};

const LIMIT_STATUS_COLORS: Record<LimitStatus, string | undefined> = {
  green: undefined,
  yellow: "#fff3cd",
  red: "#f8d7da",
};

interface BaseMultiSourceChartProps {
  startDate: Date;
  endDate: Date;
  sourceUUIDs: string[];
  title?: string;
  processDatapoints?: (
    datapoints: AdditionalDatapointData[],
  ) => AdditionalDatapointData[];
  ignoreZeroValues?: boolean;
  showPointDataAsGraph?: boolean;
  visualSettings?: VisualSettings;
  canEditVisualSettings?: boolean;
  rowIndex?: number;
  widgetIndex?: number;
  onOpenDatasourceSettings?: (
    sourceUUID: string,
    sourceName: string,
    sourceIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
}

const BaseMultiSourceChart: React.FC<BaseMultiSourceChartProps> = ({
  startDate,
  endDate,
  sourceUUIDs,
  title,
  processDatapoints,
  ignoreZeroValues,
  showPointDataAsGraph,
  visualSettings,
  canEditVisualSettings = false,
  rowIndex,
  widgetIndex,
  onOpenDatasourceSettings,
}) => {
  const chartSettings = visualSettings as ChartVisualSettings | undefined;
  const [combinedDatapoints, setSourceData] =
    useCombinedSourceData(sourceUUIDs, startDate, endDate);
  const [annotatingSource, setAnnotatingSource] =
    React.useState<GetDatasourceDataByUUID_source | null>(null);

  const datapoints = processDatapoints
    ? processDatapoints(combinedDatapoints)
    : combinedDatapoints;

  const cardStatus = React.useMemo<LimitStatus>(() => {
    let worst: LimitStatus = "green";
    combinedDatapoints.forEach((dp) => {
      const settings = chartSettings?.sourceVisualSettings?.find(
        (s) => s.sourceUUID === dp.source.source_uuid,
      );
      if (
        !settings ||
        (settings.limitWarningLow == null &&
          settings.limitCriticalLow == null &&
          settings.limitWarningHigh == null &&
          settings.limitCriticalHigh == null)
      ) {
        return;
      }
      const status = getLimitStatus(getMinMaxValues(dp.data), {
        warningLow: settings.limitWarningLow,
        criticalLow: settings.limitCriticalLow,
        warningHigh: settings.limitWarningHigh,
        criticalHigh: settings.limitCriticalHigh,
      });
      if (status === "red") {
        worst = "red";
      } else if (status === "yellow" && worst !== "red") {
        worst = "yellow";
      }
    });
    return worst;
  }, [combinedDatapoints, chartSettings]);

  const cardBackgroundColor = LIMIT_STATUS_COLORS[cardStatus];

  return (
    <Card
      className="DataSourceCard"
      style={
        cardBackgroundColor
          ? { backgroundColor: cardBackgroundColor }
          : undefined
      }
    >
      <Card.Header>{!!title && <h5>{gettext(title)}</h5>}</Card.Header>
      <Card.Body>
        {sourceUUIDs?.map((sourceUUID, idx) => (
          <FullDataSourceLoader
            key={`additional_loader_${sourceUUID}_${idx}`}
            sourceUUID={sourceUUID}
            startDate={startDate}
            endDate={endDate}
            additionalDatapoints={combinedDatapoints}
            setSourceData={setSourceData}
          />
        ))}

        {!!datapoints.length && (
          <DataChart
            source={datapoints[0].source}
            datapoints={datapoints[0].data}
            startDate={startDate}
            endDate={endDate}
            additionalDatapoints={
              datapoints.length > 1 ? datapoints.slice(1) : []
            }
            ignoreZeroValues={ignoreZeroValues}
            manualScaling={chartSettings?.axisScaleManual}
            scalingMin={chartSettings?.axisScaleMin}
            scalingMax={chartSettings?.axisScaleMax}
            sourceVisualSettings={chartSettings?.sourceVisualSettings}
            lineColors={datapoints.map((dp, idx) => {
              const sourceSettings = chartSettings?.sourceVisualSettings?.find(
                (s) => s.sourceUUID === dp.source.source_uuid,
              );
              return sourceSettings?.lineColor ?? getChartSeriesColor(idx);
            })}
          />
        )}

        {!!datapoints?.length && (
          <span className="additional-data-sources">
            {datapoints?.map(
              (
                additionalDatapointData: AdditionalDatapointData,
                idx: number,
              ) => {
                const addSource = additionalDatapointData.source;
                const addStation = additionalDatapointData.source.station;

                const sourceSettings =
                  chartSettings?.sourceVisualSettings?.find(
                    (s) => s.sourceUUID === addSource.source_uuid,
                  );

                const sourceName = formatDatasourceLabel(
                  addSource,
                  addStation,
                  sourceSettings,
                );

                const fontSize =
                  sourceSettings?.fontSize ??
                  (visualSettings?.fontSize && visualSettings.fontSize > 0
                    ? visualSettings.fontSize
                    : undefined);
                const fontColor =
                  sourceSettings?.fontColor ??
                  visualSettings?.fontColor ??
                  getChartSeriesColor(idx);
                const lineColor =
                  sourceSettings?.lineColor ?? getChartSeriesColor(idx);

                return (
                  <h6
                    key={`additional_data_source_${addSource.source_uuid}`}
                    className="additional-data-source d-flex align-items-center gap-2"
                    style={{
                      color: fontColor,
                      fontSize: fontSize ? `${fontSize}px` : undefined,
                    }}
                  >
                    <span
                      className="line-color-indicator"
                      style={{
                        display: "inline-block",
                        width: "12px",
                        height: "3px",
                        backgroundColor: lineColor,
                        borderRadius: "2px",
                      }}
                    />
                    <span>{sourceName}</span>
                    {canEditVisualSettings && onOpenDatasourceSettings && (
                      <span
                        className="edit-datasource-settings ms-1"
                        onClick={() =>
                          onOpenDatasourceSettings(
                            addSource.source_uuid,
                            sourceName,
                            idx,
                            rowIndex ?? 0,
                            widgetIndex ?? 0,
                          )
                        }
                        style={{
                          cursor: "pointer",
                          fontSize: "0.8em",
                          opacity: 0.6,
                        }}
                        title={gettext("Edit data source settings")}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </span>
                    )}
                    {canEditVisualSettings && (
                      <span
                        className="edit-datasource-settings ms-1"
                        onClick={() => setAnnotatingSource(addSource)}
                        style={{
                          cursor: "pointer",
                          fontSize: "0.8em",
                          opacity: 0.6,
                        }}
                        title={gettext("Add note")}
                      >
                        <FontAwesomeIcon icon={faNoteSticky} />
                      </span>
                    )}
                  </h6>
                );
              },
            )}
          </span>
        )}
        {annotatingSource && (
          <ChartAnnotationForm
            stationSourceUUIDs={[annotatingSource.source_uuid]}
            sourceUUID={annotatingSource.source_uuid}
            onClose={() => setAnnotatingSource(null)}
            datapoint={null}
            isAdmin={false}
            showApplyToAllStations={false}
          />
        )}
      </Card.Body>
    </Card>
  );
};

interface DailyAveragesMultiSourceChartProps {
  startDate: Date;
  endDate: Date;
  sourceUUIDs: string[];
  title?: string;
  ignoreZeroValues?: boolean;
  visualSettings?: VisualSettings;
  canEditVisualSettings?: boolean;
  rowIndex?: number;
  widgetIndex?: number;
  onOpenDatasourceSettings?: (
    sourceUUID: string,
    sourceName: string,
    sourceIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
}

export const DailyAveragesMultiSourceChart: React.FC<
  DailyAveragesMultiSourceChartProps
> = ({
  startDate,
  endDate,
  sourceUUIDs,
  title,
  ignoreZeroValues,
  visualSettings,
  canEditVisualSettings,
  rowIndex,
  widgetIndex,
  onOpenDatasourceSettings,
}) => {
  const processDatapoints = (datapoints: AdditionalDatapointData[]) =>
    datapoints.map((cp) => {
      const dailyStats = calculateDailyStats({
        datapoints: cp.data,
        startDate,
        endDate,
        ignoreZeroValues,
      });
      const formattedData = formatAsDatasourceData(dailyStats);

      return {
        data: formattedData,
        source: cp.source,
      };
    });

  return (
    <BaseMultiSourceChart
      startDate={startDate}
      endDate={endDate}
      sourceUUIDs={sourceUUIDs}
      title={title}
      processDatapoints={processDatapoints}
      ignoreZeroValues={ignoreZeroValues}
      visualSettings={visualSettings}
      canEditVisualSettings={canEditVisualSettings}
      rowIndex={rowIndex}
      widgetIndex={widgetIndex}
      onOpenDatasourceSettings={onOpenDatasourceSettings}
    />
  );
};

interface LatestValueExporterProps {
  sourceUUIDs: string[];
  title?: string;
  stationCode?: string;
  unitMapping?: {
    [key: string]: {
      unitLabel: string;
      unitModifier?: string;
    };
  };
  intervalMinutes?: IntervalUnit;
  emailSubject?: string;
  sendToEmailAddress?: string;
}

export const LatestValueExporter: React.FC<LatestValueExporterProps> = ({
  sourceUUIDs,
  title,
  stationCode,
  unitMapping,
  intervalMinutes,
  emailSubject,
  sendToEmailAddress,
}) => {
  const literalUUIDs = getLiteralArray(sourceUUIDs);

  const { data: datasources, loading } = useCachedSubscription<
    GetDatasourcesByUUID,
    GetDatasourcesByUUIDVariables
  >(GET_DATASOURCES_BY_UUID, {
    variables: {
      uuids: literalUUIDs,
    },
  });

  const sourceList = datasources?.source ?? [];
  const sourceListLoading = loading || !datasources;
  if (sourceListLoading) {
    <Card>
      <Card.Body>
        <Spinner animation="border" />
      </Card.Body>
    </Card>;
  }

  return (
    <Card className="DataSourceCard">
      <Card.Header>{!!title && <h5>{gettext(title)}</h5>}</Card.Header>
      <Card.Body>
        <h6>{gettext("Exporter Configuration")}</h6>
        <ul>
          <li>
            {gettext("Interval")}: {intervalMinutes} minutes
          </li>
          <li>
            {gettext("Recipient email")}: {sendToEmailAddress}
          </li>
          <li>
            {gettext("Email subject")}: {emailSubject}
          </li>
          <li>
            {gettext("Station code")}: {stationCode}
          </li>
          <li>
            {gettext("Data sources")}:{" "}
            {sourceList.map((source) => {
              const originalUnitLabel = source.unit?.symbol ?? "";
              const unitMappingRecord = (unitMapping?.[source.source_uuid] ??
                {}) as any;
              const unitLabel =
                unitMappingRecord.unitLabel ?? originalUnitLabel;
              const unitModifier = unitMappingRecord.unitModifier ?? 1;
              return (
                <div
                  key={source.source_uuid}
                  className="exporter-source-config"
                >
                  <span className="exporter-source-name">
                    {`${source.station?.name} (${source.station?.identifier}) - ${source.name} [${originalUnitLabel}]`}
                  </span>
                  <span>
                    {` ${gettext("Render unit as")}: `}
                    <b>{`[${unitLabel}]`}</b>
                  </span>
                  <span>
                    {` ${gettext("Value")}: `}
                    {`x ${unitModifier}`}
                  </span>
                </div>
              );
            })}
          </li>
        </ul>
      </Card.Body>
    </Card>
  );
};

interface AccruedDifferenceChartProps {
  startDate: Date;
  endDate: Date;
  sourceUUIDs: string[];
  title?: string;
  ignoreZeroValues?: boolean;
}

const AccruedDifferenceChart: React.FC<AccruedDifferenceChartProps> = ({
  startDate,
  endDate,
  sourceUUIDs,
  title,
  ignoreZeroValues,
}) => {
  const [combinedDatapoints, setSourceData] =
    useCombinedSourceData(sourceUUIDs, startDate, endDate);

  const processDatapoints = (datapoints: AdditionalDatapointData[]) =>
    datapoints.map((cp) => {
      const processedData = calculateAccumulationDifferences({
        datapoints: cp.data,
        startDate,
        endDate,
        ignoreZeroValues,
      });

      return {
        data: processedData,
        source: cp.source,
      };
    });

  const datapoints = processDatapoints(combinedDatapoints);

  const periodTotals = React.useMemo(() => {
    return datapoints.map((dp) => {
      const total = dp.data.reduce((sum, point) => sum + point.value, 0);
      return { source: dp.source, total };
    });
  }, [datapoints]);

  return (
    <Card className="DataSourceCard">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            {!!title && <h5 className="mb-0">{gettext(title)}</h5>}
            {!!periodTotals.length && (
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted">{gettext("Period Total")}:</small>
                {periodTotals.map((total, idx) => (
                  <div key={`period_total_${total.source.source_uuid}`}>
                    <span style={{ fontSize: "1.1rem", fontWeight: "bold" }}>
                      {formatNumberSafe(total.total, 2)}
                    </span>
                    {total.source.unit?.symbol && (
                      <span style={{ fontSize: "0.9rem", marginLeft: "4px" }}>
                        {total.source.unit.symbol}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div />
        </div>
      </Card.Header>
      <Card.Body>
        {sourceUUIDs?.map((sourceUUID, idx) => (
          <FullDataSourceLoader
            key={`additional_loader_${sourceUUID}_${idx}`}
            sourceUUID={sourceUUID}
            startDate={startDate}
            endDate={endDate}
            additionalDatapoints={combinedDatapoints}
            setSourceData={setSourceData}
          />
        ))}

        {!!datapoints.length && (
          <DataChart
            source={datapoints[0].source}
            datapoints={datapoints[0].data}
            startDate={startDate}
            endDate={endDate}
            additionalDatapoints={
              datapoints.length > 1 ? datapoints.slice(1) : []
            }
            ignoreZeroValues={ignoreZeroValues}
          />
        )}

        {!!datapoints?.length && (
          <span className="additional-data-sources">
            {datapoints?.map(
              (
                additionalDatapointData: AdditionalDatapointData,
                idx: number,
              ) => {
                const addSource = additionalDatapointData.source;
                const addStation = additionalDatapointData.source.station;
                const sourceName = `${addStation?.name} (${
                  addStation?.identifier
                }) - ${addSource.name} ${getSourceUnitSymbol(addSource)}`;
                return (
                  <h6
                    key={`additional_data_source_${addSource.source_uuid}`}
                    className="additional-data-source"
                    style={{ color: getChartSeriesColor(idx) }}
                  >
                    {sourceName}
                  </h6>
                );
              },
            )}
          </span>
        )}
      </Card.Body>
    </Card>
  );
};

const calculateAccumulationDifferences = ({
  datapoints,
  startDate,
  endDate,
  ignoreZeroValues,
}: {
  datapoints: GetDatasourceData_source_data[];
  startDate: Date;
  endDate: Date;
  ignoreZeroValues?: boolean;
}): GetDatasourceData_source_data[] => {
  const validDatapoints =
    datapoints?.filter(
      (point) => !point.invalid && (!ignoreZeroValues || point.value !== 0),
    ) ?? [];

  if (validDatapoints.length === 0) {
    return [];
  }

  const timeRangeMs = endDate.getTime() - startDate.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const monthMs = 31 * dayMs;
  const yearMs = 365 * dayMs;

  if (timeRangeMs <= dayMs) {
    return calculateHourlyDifferences(validDatapoints, startDate, endDate);
  } else if (timeRangeMs < monthMs) {
    return calculateDailyDifferences(validDatapoints, startDate, endDate);
  } else if (timeRangeMs < yearMs) {
    return calculateMonthlyDifferences(validDatapoints, startDate, endDate);
  } else {
    return calculateYearlyDifferences(validDatapoints, startDate, endDate);
  }
};

const calculateHourlyDifferences = (
  datapoints: GetDatasourceData_source_data[],
  startDate: Date,
  endDate: Date,
): GetDatasourceData_source_data[] => {
  const hourlyData: GetDatasourceData_source_data[] = [];

  const currentHour = new Date(startDate);
  currentHour.setMinutes(0, 0, 0);

  while (currentHour < endDate) {
    const nextHour = new Date(currentHour.getTime() + 60 * 60 * 1000);

    const periodStart = findClosestReading(datapoints, currentHour, true);
    const periodEnd = findClosestReading(datapoints, nextHour, false);

    if (periodStart && periodEnd) {
      const difference = periodEnd.value - periodStart.value;

      hourlyData.push({
        timestamp: nextHour.toISOString(),
        value: Math.max(0, difference),
        invalid: false,
        __typename: "data",
      });
    }

    currentHour.setTime(nextHour.getTime());
  }

  return hourlyData;
};

const calculateDailyDifferences = (
  datapoints: GetDatasourceData_source_data[],
  startDate: Date,
  endDate: Date,
): GetDatasourceData_source_data[] => {
  const dailyData: GetDatasourceData_source_data[] = [];

  const currentDay = new Date(startDate);
  currentDay.setHours(0, 0, 0, 0);

  while (currentDay < endDate) {
    const nextDay = new Date(currentDay.getTime() + 24 * 60 * 60 * 1000);

    const periodStart = findClosestReading(datapoints, currentDay, true);
    const periodEnd = findClosestReading(datapoints, nextDay, false);

    if (periodStart && periodEnd) {
      const difference = periodEnd.value - periodStart.value;

      dailyData.push({
        timestamp: nextDay.toISOString(),
        value: Math.max(0, difference),
        invalid: false,
        __typename: "data",
      });
    }

    currentDay.setTime(nextDay.getTime());
  }

  return dailyData;
};

const calculateMonthlyDifferences = (
  datapoints: GetDatasourceData_source_data[],
  startDate: Date,
  endDate: Date,
): GetDatasourceData_source_data[] => {
  const monthlyData: GetDatasourceData_source_data[] = [];

  const currentMonth = new Date(startDate);
  currentMonth.setDate(1);
  currentMonth.setHours(0, 0, 0, 0);

  while (currentMonth < endDate) {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    const periodStart = findClosestReading(datapoints, currentMonth, true);
    const periodEnd = findClosestReading(datapoints, nextMonth, false);

    if (periodStart && periodEnd) {
      const difference = periodEnd.value - periodStart.value;

      monthlyData.push({
        timestamp: nextMonth.toISOString(),
        value: Math.max(0, difference),
        invalid: false,
        __typename: "data",
      });
    }

    currentMonth.setTime(nextMonth.getTime());
  }

  return monthlyData;
};

const calculateYearlyDifferences = (
  datapoints: GetDatasourceData_source_data[],
  startDate: Date,
  endDate: Date,
): GetDatasourceData_source_data[] => {
  const yearlyData: GetDatasourceData_source_data[] = [];

  const currentYear = new Date(startDate);
  currentYear.setMonth(0, 1);
  currentYear.setHours(0, 0, 0, 0);

  while (currentYear < endDate) {
    const nextYear = new Date(currentYear);
    nextYear.setFullYear(nextYear.getFullYear() + 1);

    const periodStart = findClosestReading(datapoints, currentYear, true);
    const periodEnd = findClosestReading(datapoints, nextYear, false);

    if (periodStart && periodEnd) {
      const difference = periodEnd.value - periodStart.value;

      yearlyData.push({
        timestamp: nextYear.toISOString(),
        value: Math.max(0, difference),
        invalid: false,
        __typename: "data",
      });
    }

    currentYear.setTime(nextYear.getTime());
  }

  return yearlyData;
};

const findClosestReading = (
  datapoints: GetDatasourceData_source_data[],
  targetTime: Date,
  preferEarlier: boolean = true,
): GetDatasourceData_source_data | null => {
  if (datapoints.length === 0) return null;

  let closest = datapoints[0];
  let minDiff = Math.abs(
    new Date(closest.timestamp).getTime() - targetTime.getTime(),
  );

  for (const point of datapoints) {
    const pointTime = new Date(point.timestamp).getTime();
    const targetTimeMs = targetTime.getTime();
    const diff = Math.abs(pointTime - targetTimeMs);

    if (diff < minDiff) {
      closest = point;
      minDiff = diff;
    } else if (diff === minDiff) {
      if (preferEarlier && pointTime < targetTimeMs) {
        closest = point;
      } else if (!preferEarlier && pointTime > targetTimeMs) {
        closest = point;
      }
    }
  }

  return closest;
};

interface MultiSourceChartProps {
  startDate: Date;
  endDate: Date;
  sourceUUIDs: string[];
  title?: string;
  ignoreZeroValues?: boolean;
  showPointDataAsGraph?: boolean;
  visualSettings?: VisualSettings;
  canEditVisualSettings?: boolean;
  rowIndex?: number;
  widgetIndex?: number;
  onOpenDatasourceSettings?: (
    sourceUUID: string,
    sourceName: string,
    sourceIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
}

export const MultiSourceChart: React.FC<MultiSourceChartProps> = ({
  startDate,
  endDate,
  sourceUUIDs,
  title,
  ignoreZeroValues,
  showPointDataAsGraph,
  visualSettings,
  canEditVisualSettings,
  rowIndex,
  widgetIndex,
  onOpenDatasourceSettings,
}) => (
  <BaseMultiSourceChart
    startDate={startDate}
    endDate={endDate}
    sourceUUIDs={sourceUUIDs}
    title={title}
    ignoreZeroValues={ignoreZeroValues}
    showPointDataAsGraph={showPointDataAsGraph}
    visualSettings={visualSettings}
    canEditVisualSettings={canEditVisualSettings}
    rowIndex={rowIndex}
    widgetIndex={widgetIndex}
    onOpenDatasourceSettings={onOpenDatasourceSettings}
  />
);

interface PrimaryParameterChartProps {
  flow: GetStationDashboardData_flow;
  datapoints: GetDatasourceData_source_data[];
  startDate: Date;
  endDate: Date;
  ignoreZeroValues?: boolean;
  showPointDataAsGraph?: boolean;
}
const PrimaryParameterChart: React.FC<PrimaryParameterChartProps> = ({
  flow,
  datapoints,
  startDate,
  endDate,
  ignoreZeroValues,
  showPointDataAsGraph,
}) => {
  return (
    <Card className="DataSourceCard">
      <Card.Header>
        <h5>
          {flow.name} [{flow.unit?.symbol}]
        </h5>
      </Card.Header>
      <Card.Body>
        <DataChart
          source={flow}
          datapoints={datapoints}
          startDate={startDate}
          endDate={endDate}
          ignoreZeroValues={ignoreZeroValues}
          showPointDataAsGraph={showPointDataAsGraph}
        />
      </Card.Body>
    </Card>
  );
};

interface IframeWidgetProps {
  title: string;
  sourceUrl: string;
  aspectWidth: number;
  aspectHeight: number;
}
const IframeWidget: React.FC<IframeWidgetProps> = ({
  title,
  sourceUrl,
  aspectWidth,
  aspectHeight,
}) => {
  const aspectRatio = (aspectHeight * 100) / aspectWidth;

  return (
    <Card className="DataSourceCard">
      <Card.Header>
        <h5>{gettext(title)}</h5>
      </Card.Header>
      <Card.Body
        className="IframeBody"
        style={{
          paddingTop: `${aspectRatio}%`,
        }}
      >
        <iframe title={title} src={sourceUrl}></iframe>
      </Card.Body>
    </Card>
  );
};

interface BurrelCameraWidgetProps {
  title: string;
  cameraId: number;
}

const BurrelCameraWidget: React.FC<BurrelCameraWidgetProps> = ({
  title,
  cameraId,
}) => {
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!cameraId) return;

    let objectUrl: string | null = null;
    let cancelled = false;

    // Fetched through apiClient so the bearer token is sent; an img tag on its
    // own cannot authenticate against the API.
    const load = async () => {
      try {
        const resp = await apiClient.requestRaw(
          `/burrel/cameras/${cameraId}/image`,
        );
        if (!resp.ok) {
          throw new Error(String(resp.status));
        }
        const blob = await resp.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [cameraId]);

  return (
    <Card className="DataSourceCard">
      <Card.Header>
        <h5>{gettext(title)}</h5>
      </Card.Header>
      <Card.Body>
        {imageUrl ? (
          <img src={imageUrl} alt={title} style={{ width: "100%" }} />
        ) : (
          <p>
            {failed ? gettext("No image available") : gettext("Loading...")}
          </p>
        )}
      </Card.Body>
    </Card>
  );
};

interface SingleWidgetConfig {
  key: string;
  metaData: Record<string, any>;
  visualSettings?: VisualSettings;
}

interface StationDashboardProps {
  stationIdentifier: string;
  stationId: number;
  sourceId: number;
  sourceType: string;
  startDate: Date;
  endDate: Date;
  intervalChoices?: [string, number][];
  interval?: IntervalUnit;
  editedStructure: any[] | null;
  setEditedStructure: React.Dispatch<React.SetStateAction<any[] | null>>;
  setChangesMade: React.Dispatch<React.SetStateAction<boolean>>;
  canEditWidgets?: boolean;
  showPointDataAsGraph?: boolean;
  singleWidget?: SingleWidgetConfig;
  canEditVisualSettings?: boolean;
  onOpenDatasourceSettings?: (
    sourceUUID: string,
    sourceName: string,
    sourceIndex: number,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
  onCopyWidget?: (
    widget: RenderWidget,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
}

interface DraggableWidgetProps {
  widget: RenderWidget;
  scope: string;
  idx: number;
  widgetGroupIdx: number;
  widgetRender: RenderWidget[];
  canEditWidgets: boolean;
  isTouchDevice: boolean;
  isMobileViewport: boolean;
  stationWidgetRows: any[];
  handleWidgetResize: (
    rowIndex: number,
    widgetIndex: number,
    newColSpan: number,
  ) => void;
  showEditWidget: (widget: any, rowIndex: number, widgetIndex: number) => void;
  onDeleteWidget: (idx: number, widgetGroupIdx: number) => void;
  onCopyWidget?: (
    widget: RenderWidget,
    rowIndex: number,
    widgetIndex: number,
  ) => void;
  moveWidget: (
    widgetIdx: number,
    rowIdx: number,
    direction: "left" | "right" | "up" | "down",
    isMobileViewport?: boolean,
  ) => void;
}

// Shown while editing in place of widgets whose type no longer exists
const unknownWidgetPlaceholder = (key: string): AvailableWidget => ({
  key,
  component: () => (
    <Card>
      <Card.Body>
        <p className="mb-0 text-muted">
          {gettext("Widget type not found")} ({key})
        </p>
      </Card.Body>
    </Card>
  ),
});

const DraggableWidget: React.FC<DraggableWidgetProps> = React.memo(
  ({
    widget,
    scope,
    idx,
    widgetGroupIdx,
    widgetRender,
    canEditWidgets,
    isTouchDevice,
    isMobileViewport,
    stationWidgetRows,
    handleWidgetResize,
    showEditWidget,
    onDeleteWidget,
    onCopyWidget,
    moveWidget,
  }) => {
    const isDragging = useDragSelector((state) => state.isDragging);
    const dragPayload = useDragSelector((state) => state.dragPayload);

    const widgetUUIDs = widget.metaData?.sourceUUIDs?.join(",");
    const widgetUUID =
      widget.sourceUUID ?? widget.metaData?.sourceUUID ?? widgetUUIDs ?? 0;

    const isDraggingInScope = isDragging && dragPayload?.scope === scope;

    const isBeingDragged =
      dragPayload?.scope === scope &&
      dragPayload?.widgetIdx === idx &&
      dragPayload?.widgetGroupIdx === widgetGroupIdx;

    const isDragTarget = isDraggingInScope && !isBeingDragged;

    // Drag the stored widget, not the render wrapper, so that moving it keeps
    // all of its persisted fields.
    const storedWidget = stationWidgetRows[widgetGroupIdx]?.widgets?.[idx];
    const { ref: dragRef } = useDraggable({
      payload: {
        widget: storedWidget,
        widgetIdx: idx,
        widgetGroupIdx,
        scope,
      },
      disabled: !canEditWidgets || isTouchDevice || !storedWidget,
    });

    const colSpan = widget.colSpan ?? DEFAULT_COL_SPAN;
    const dragWidth = isDraggingInScope
      ? calculateWidgetWidthDuringDrag(widget, widgetRender, DEFAULT_COL_SPAN)
      : undefined;

    const WidgetWrapper = dragWidth ? "div" : Col;
    const widgetWrapperProps = dragWidth
      ? {
          className: classNames("widget-column", {
            "widget-dragging": isBeingDragged,
            "widget-drag-mode": isDraggingInScope,
          }),
          style: {
            opacity: isBeingDragged ? 0.4 : 1,
            flex: `0 0 ${dragWidth}`,
            maxWidth: dragWidth,
            minWidth: 0,
            transition:
              "opacity 0.2s ease, flex 0.2s ease, max-width 0.2s ease",
          },
        }
      : {
          md: colSpan,
          xs: 12,
          className: classNames("widget-column", {
            "widget-dragging": isBeingDragged,
            "widget-drag-mode": isDraggingInScope,
          }),
          style: {
            opacity: isBeingDragged ? 0.4 : 1,
            transition:
              "opacity 0.2s ease, flex 0.2s ease, max-width 0.2s ease",
          },
        };

    const widgetContent = (
      <WidgetWrapper
        key={`widget_column_${widget.key}_${idx}_${widgetUUID}`}
        {...widgetWrapperProps}
      >
        {canEditWidgets && (
          <div
            className={classNames(
              "widget-header d-flex align-items-center justify-content-between px-3 py-2",
              { "widget-dragging": isBeingDragged },
            )}
          >
            <div
              ref={dragRef as React.RefCallback<HTMLDivElement>}
              className="grip-icon-wrapper"
              style={{
                cursor: canEditWidgets && !isTouchDevice ? "grab" : undefined,
              }}
            >
              <FontAwesomeIcon icon={faGripVertical} className="grip-icon" />
            </div>
            <div className="d-flex align-items-center gap-3">
              <Dropdown className="resize-dropdown">
                <Dropdown.Toggle
                  as="div"
                  className="widget-action-btn resize-button"
                  title={gettext("Change widget size")}
                >
                  <FontAwesomeIcon icon={getSizePreset(widget.colSpan).icon} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {SIZE_PRESETS.map((preset) => (
                    <Dropdown.Item
                      key={preset.key}
                      active={widget.colSpan === preset.colSpan}
                      onClick={() =>
                        handleWidgetResize(widgetGroupIdx, idx, preset.colSpan)
                      }
                    >
                      <FontAwesomeIcon icon={preset.icon} className="me-2" />
                      {preset.label}
                      {preset.colSpan === DEFAULT_COL_SPAN && (
                        <span className="text-muted ms-1">
                          ({gettext("default")})
                        </span>
                      )}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              {onCopyWidget && (
                <div
                  className="widget-action-btn"
                  onClick={() => onCopyWidget(widget, widgetGroupIdx, idx)}
                  title={gettext("Copy to dashboard")}
                >
                  <FontAwesomeIcon icon={faCopy} />
                </div>
              )}
              <div
                className="widget-action-btn"
                onClick={() => {
                  const originalWidget =
                    stationWidgetRows[widgetGroupIdx]?.widgets[idx];
                  showEditWidget(originalWidget, widgetGroupIdx, idx);
                }}
              >
                <FontAwesomeIcon icon={faEdit} />
              </div>
              <div
                className="widget-action-btn delete-btn"
                onClick={() => onDeleteWidget(idx, widgetGroupIdx)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </div>
            </div>
          </div>
        )}

        {canEditWidgets && isTouchDevice && (
          <div className="widget-arrow-controls">
            <button
              className="arrow-btn arrow-left desktop-only"
              onClick={() =>
                moveWidget(idx, widgetGroupIdx, "left", isMobileViewport)
              }
              disabled={idx === 0}
              title="Move left"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <button
              className="arrow-btn arrow-up"
              onClick={() =>
                moveWidget(idx, widgetGroupIdx, "up", isMobileViewport)
              }
              disabled={
                isMobileViewport
                  ? idx === 0 && widgetGroupIdx === 0
                  : widgetGroupIdx === 0 && widgetRender.length === 1
              }
              title={isMobileViewport ? "Move up / left" : "Move up"}
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </button>
            <button
              className="arrow-btn arrow-down"
              onClick={() =>
                moveWidget(idx, widgetGroupIdx, "down", isMobileViewport)
              }
              disabled={
                isMobileViewport
                  ? widgetGroupIdx >= (stationWidgetRows?.length ?? 0) - 1 &&
                    idx >= widgetRender.length - 1
                  : widgetGroupIdx >= (stationWidgetRows?.length ?? 0) - 1 &&
                    widgetRender.length === 1
              }
              title={isMobileViewport ? "Move down / right" : "Move down"}
            >
              <FontAwesomeIcon icon={faArrowDown} />
            </button>
            <button
              className="arrow-btn arrow-right desktop-only"
              onClick={() =>
                moveWidget(idx, widgetGroupIdx, "right", isMobileViewport)
              }
              disabled={idx >= widgetRender.length - 1}
              title="Move right"
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        )}

        {widget.component?.(
          widget.sourceUUID ?? widget.metaData?.sourceUUID,
          widget.metaData,
          widget.visualSettings,
          { rowIndex: widgetGroupIdx, widgetIndex: idx },
        )}
      </WidgetWrapper>
    );

    const draggedWidgetIdx = dragPayload?.widgetIdx;
    const draggedRowIdx = dragPayload?.widgetGroupIdx;
    const isSameRowAsDragged = draggedRowIdx === widgetGroupIdx;

    const showLeftZone =
      isDragTarget && !(isSameRowAsDragged && draggedWidgetIdx === idx - 1);
    const showRightZone =
      isDragTarget && !(isSameRowAsDragged && draggedWidgetIdx === idx + 1);

    return (
      <React.Fragment
        key={`widget_fragment_${widget.key}_${idx}_${widgetUUID}`}
      >
        {showLeftZone && (
          <WidgetDropZone
            rowIndex={widgetGroupIdx}
            widgetIndex={idx}
            side="left"
            isFirst={true}
            scope={scope}
          />
        )}
        {widgetContent}
        {showRightZone && (
          <WidgetDropZone
            rowIndex={widgetGroupIdx}
            widgetIndex={idx}
            side="right"
            isFirst={false}
            scope={scope}
          />
        )}
      </React.Fragment>
    );
  },
);

DraggableWidget.displayName = "DraggableWidget";

const StationDashboard: React.FC<StationDashboardProps> = ({
  stationIdentifier,
  stationId,
  sourceId,
  sourceType,
  startDate,
  endDate,
  interval,
  intervalChoices = [],
  editedStructure,
  setEditedStructure,
  setChangesMade,
  canEditWidgets = false,
  showPointDataAsGraph = false,
  singleWidget,
  canEditVisualSettings = false,
  onOpenDatasourceSettings,
  onCopyWidget,
}: StationDashboardProps) => {
  const [isTouchDevice] = React.useState(
    () => "ontouchstart" in window || navigator.maxTouchPoints > 0,
  );
  const isMobileViewport = useMediaQuery("(max-width: 767px)");
  const isDragging = useDragSelector(
    (state) =>
      state.isDragging && state.dragPayload?.scope === stationIdentifier,
  );

  const { setActiveModal, setModalPayload } = useModalContext();
  const showAddWidget = React.useMemo(
    () => (e: any) => {
      setActiveModal("add-dashboard-widget");
      setModalPayload({
        stationIdentifier,
      });
    },
    [setActiveModal, setModalPayload, stationIdentifier],
  );

  const showEditWidget = React.useCallback(
    (widget: any, rowIndex: number, widgetIndex: number) => {
      setActiveModal("edit-dashboard-widget");
      setModalPayload({
        stationIdentifier,
        editingWidget: {
          widget,
          rowIndex,
          widgetIndex,
        },
      });
    },
    [setActiveModal, setModalPayload, stationIdentifier],
  );

  const stationIdx = editedStructure
    ?.map((structureRow: any) => structureRow.station.identifier)
    .indexOf(stationIdentifier);
  const stationStructure = editedStructure?.find(
    (structureRow: any) =>
      structureRow.station.identifier === stationIdentifier,
  );
  const stationWidgetRows = stationStructure?.station?.widget_rows ?? null;

  const { data } = useCachedSubscription<
    GetStationDashboardData,
    GetStationDashboardDataVariables
  >(GET_STATION_DASHBOARD_DATA, {
    variables: {
      startDate,
      endDate,
      sourceType,
      sourceId,
    },
    skip: singleWidget !== undefined,
  });

  const AVAILABLE_WIDGETS: AvailableWidget[] = React.useMemo(() => {
    const flow = data?.flow ?? null;
    const datapoints = data?.flow?.data ?? [];

    return [
      {
        key: "iframe_widget",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <IframeWidget
            title={metaData.title}
            sourceUrl={metaData.sourceUrl}
            aspectWidth={metaData.aspectWidth}
            aspectHeight={metaData.aspectHeight}
          />
        ),
      },
      {
        key: "burrel_camera_widget",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <BurrelCameraWidget
            title={metaData.title}
            cameraId={metaData.cameraId}
          />
        ),
      },
      {
        key: "primary_parameter_chart",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <PrimaryParameterChart
            flow={flow!}
            datapoints={datapoints}
            startDate={startDate}
            endDate={endDate}
            ignoreZeroValues={!!metaData.ignoreZeroValues}
            showPointDataAsGraph={showPointDataAsGraph}
          />
        ),
      },
      {
        key: "data_source_chart",
        component: (
          sourceUUID: string | undefined,
          metaData: any = {},
          visualSettings?: VisualSettings,
          widgetContext?: { rowIndex: number; widgetIndex: number },
        ) => (
          <MultiSourceChart
            startDate={startDate}
            endDate={endDate}
            title={metaData.title}
            sourceUUIDs={metaData.sourceUUIDs}
            ignoreZeroValues={!!metaData.ignoreZeroValues}
            showPointDataAsGraph={showPointDataAsGraph}
            visualSettings={visualSettings}
            canEditVisualSettings={canEditVisualSettings}
            rowIndex={widgetContext?.rowIndex}
            widgetIndex={widgetContext?.widgetIndex}
            onOpenDatasourceSettings={onOpenDatasourceSettings}
          />
        ),
      },
      {
        key: "accrued_difference_chart",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <AccruedDifferenceChart
            startDate={startDate}
            endDate={endDate}
            title={metaData.title}
            sourceUUIDs={metaData.sourceUUIDs}
            ignoreZeroValues={!!metaData.ignoreZeroValues}
          />
        ),
      },
      {
        key: "latest_value_exporter",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <LatestValueExporter
            title={metaData.title}
            sourceUUIDs={metaData.sourceUUIDs}
            unitMapping={metaData.unitMapping}
            stationCode={metaData.stationCode}
            sendToEmailAddress={metaData.sendToEmailAddress}
            intervalMinutes={metaData.intervalMinutes}
            emailSubject={metaData.emailSubject}
          />
        ),
      },
      {
        key: "stats_summary_widget",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <StatsSummaryWidget
            flow={flow!}
            datapoints={datapoints}
            endDate={endDate}
            ignoreZeroValues={!!metaData.ignoreZeroValues}
          />
        ),
      },
      {
        key: "primary_parameter_average_chart",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <PrimaryParameterAverageChart
            flow={flow!}
            startDate={startDate}
            endDate={endDate}
            datapoints={datapoints}
            ignoreZeroValues={!!metaData.ignoreZeroValues}
          />
        ),
      },
      {
        key: "daily_averages_table",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <DailyAveragesTable
            datapoints={datapoints}
            startDate={startDate}
            endDate={endDate}
            ignoreZeroValues={!!metaData.ignoreZeroValues}
          />
        ),
      },
      {
        key: "daily_averages_table_multi_source",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <DailyAveragesTableMultiSource
            title={metaData.title}
            startDate={startDate}
            endDate={endDate}
            sourceUUIDs={metaData.sourceUUIDs}
            ignoreZeroValues={!!metaData.ignoreZeroValues}
          />
        ),
      },
      {
        key: "daily_averages_multi_parameter",
        component: (
          sourceUUID: string | undefined,
          metaData: any = {},
          visualSettings?: VisualSettings,
          widgetContext?: { rowIndex: number; widgetIndex: number },
        ) => (
          <DailyAveragesMultiSourceChart
            startDate={startDate}
            endDate={endDate}
            title={metaData.title}
            sourceUUIDs={metaData.sourceUUIDs}
            ignoreZeroValues={!!metaData.ignoreZeroValues}
            visualSettings={visualSettings}
            canEditVisualSettings={canEditVisualSettings}
            rowIndex={widgetContext?.rowIndex}
            widgetIndex={widgetContext?.widgetIndex}
            onOpenDatasourceSettings={onOpenDatasourceSettings}
          />
        ),
      },
      {
        key: "manual_lab_data_table",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <ManualLabDataTable
            title={metaData.title}
            startDate={startDate}
            endDate={endDate}
            sourceUUIDs={metaData.sourceUUIDs}
            layoutOption={metaData.layoutOption}
          />
        ),
      },
      {
        key: "temperature_profile_widget",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <TemperatureProfileWidget
            stationId={stationId}
            startDate={startDate}
            endDate={endDate}
            metaData={metaData}
          />
        ),
      },
      {
        key: "total_hourly_discharge_table",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <HourlyDischargeTable
            startDate={startDate}
            endDate={endDate}
            title={metaData.title}
            displayUnit={metaData.displayUnit}
            sourceUUIDs={metaData.sourceUUIDs}
          />
        ),
      },
      ...(["daily", "monthly", "annual"] as const).map((period) => ({
        key: `total_${period}_discharge_table`,
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <TotalDischargeTable
            period={period}
            startDate={startDate}
            endDate={endDate}
            title={metaData.title}
            displayUnit={metaData.displayUnit}
            sourceUUIDs={metaData.sourceUUIDs}
          />
        ),
      })),
      {
        key: "comment_widget",
        component: (sourceUUID: string | undefined, metaData: any = {}) => (
          <CommentWidget
            title={metaData.title || ""}
            content={metaData.content || ""}
            fontSize={metaData.fontSize}
            fontColor={metaData.fontColor}
            backgroundColor={metaData.backgroundColor}
            editMode={false}
          />
        ),
      },
      {
        key: "load_calculation_widget",
        component: (sourceUUID: string | undefined, metaData: any = {}) => {
          const { dashboardId, widgetConfig } = metaData;
          return (
            <LoadCalculationWidget
              title={metaData.title}
              widgetId={metaData.widgetId}
              dashboardId={dashboardId}
              metaData={metaData}
              startDate={startDate}
              endDate={endDate}
              preview={!canEditWidgets}
              widgetConfig={widgetConfig}
            />
          );
        },
      },
    ];
  }, [
    data,
    startDate,
    endDate,
    stationId,
    showPointDataAsGraph,
    canEditVisualSettings,
    onOpenDatasourceSettings,
    canEditWidgets,
  ]);

  const handleDropOnZone = React.useCallback(
    (draggingWidget: DragPayload, position: DropPosition) => {
      if (!stationWidgetRows) {
        return;
      }

      const { newWidgetRows, hasChanges } = calculateWidgetDrop(
        stationWidgetRows,
        draggingWidget,
        position,
      );

      if (!hasChanges) {
        return;
      }

      const filteredStructure = insertIntoList(
        editedStructure,
        stationIdx,
        {
          hiddenTitle: stationStructure?.hiddenTitle,
          station: {
            ...stationStructure?.station,
            widget_rows: newWidgetRows,
          },
        },
        true,
      );

      setEditedStructure(filteredStructure);
      setChangesMade(true);
    },
    [
      stationWidgetRows,
      editedStructure,
      stationIdx,
      stationStructure,
      setEditedStructure,
      setChangesMade,
    ],
  );

  // Every station on the page receives every drop; only handle the ones that
  // start and end within this station.
  useScopedDragEnd(
    stationIdentifier,
    handleDropOnZone,
    singleWidget === undefined && canEditWidgets,
  );

  const moveWidget = React.useCallback(
    (
      widgetIdx: number,
      rowIdx: number,
      direction: "left" | "right" | "up" | "down",
      isMobileViewport: boolean = false,
    ) => {
      const widget = stationWidgetRows?.[rowIdx]?.widgets?.[widgetIdx];
      if (!widget) {
        return;
      }

      const payload: DragPayload = {
        widget,
        widgetIdx,
        widgetGroupIdx: rowIdx,
        scope: stationIdentifier,
      };

      const rowWidgets = stationWidgetRows?.[rowIdx]?.widgets ?? [];
      const totalRows = stationWidgetRows?.length ?? 0;
      const isFirstInRow = widgetIdx === 0;
      const isLastInRow = widgetIdx >= rowWidgets.length - 1;

      if (direction === "left" && widgetIdx > 0) {
        handleDropOnZone(payload, {
          type: "widget",
          rowIndex: rowIdx,
          widgetIndex: widgetIdx - 1,
          side: "left",
        });
      } else if (direction === "right" && !isLastInRow) {
        handleDropOnZone(payload, {
          type: "widget",
          rowIndex: rowIdx,
          widgetIndex: widgetIdx + 1,
          side: "right",
        });
      } else if (direction === "up") {
        if (isMobileViewport && !isFirstInRow) {
          handleDropOnZone(payload, {
            type: "widget",
            rowIndex: rowIdx,
            widgetIndex: widgetIdx - 1,
            side: "left",
          });
        } else if (isMobileViewport && isFirstInRow && rowIdx > 0) {
          const prevRowWidgets = stationWidgetRows?.[rowIdx - 1]?.widgets ?? [];
          const targetIndex = Math.max(0, prevRowWidgets.length - 2);
          handleDropOnZone(payload, {
            type: "widget",
            rowIndex: rowIdx - 1,
            widgetIndex: targetIndex,
            side: "left",
          });
        } else if (!isMobileViewport && rowIdx > 0) {
          handleDropOnZone(payload, {
            type: "row",
            rowIndex: rowIdx - 1,
            isNewRow: false,
          });
        } else if (!isMobileViewport && rowIdx === 0) {
          handleDropOnZone(payload, {
            type: "row",
            rowIndex: 0,
            isNewRow: true,
          });
        }
      } else if (direction === "down") {
        if (isMobileViewport && !isLastInRow) {
          handleDropOnZone(payload, {
            type: "widget",
            rowIndex: rowIdx,
            widgetIndex: widgetIdx + 1,
            side: "right",
          });
        } else if (isMobileViewport && isLastInRow && rowIdx < totalRows - 1) {
          const nextRowWidgets = stationWidgetRows?.[rowIdx + 1]?.widgets ?? [];
          const targetIndex = nextRowWidgets.length > 0 ? 1 : 0;
          handleDropOnZone(payload, {
            type: "widget",
            rowIndex: rowIdx + 1,
            widgetIndex: targetIndex,
            side: "left",
          });
        } else if (!isMobileViewport && rowIdx < totalRows - 1) {
          handleDropOnZone(payload, {
            type: "row",
            rowIndex: rowIdx + 1,
            isNewRow: false,
          });
        } else if (!isMobileViewport && rowIdx >= totalRows - 1) {
          handleDropOnZone(payload, {
            type: "row",
            rowIndex: rowIdx + 1,
            isNewRow: true,
          });
        }
      }
    },
    [stationWidgetRows, handleDropOnZone, stationIdentifier],
  );

  const onDeleteWidget = (idx: number, widgetGroupIdx: number) => {
    const newGroupWidgets = stationWidgetRows?.[widgetGroupIdx]?.widgets ?? [];
    const newWidgetGroup = newGroupWidgets.filter(
      (_: any, widgetIdx: number) => widgetIdx !== idx,
    );
    const newStructure = insertIntoList(
      stationWidgetRows,
      widgetGroupIdx,
      {
        widgets: newWidgetGroup,
      },
      true,
    );

    const filteredStructure = insertIntoList(
      editedStructure,
      stationIdx,
      {
        station: {
          ...stationStructure?.station,
          widget_rows: newStructure,
        },
      },
      true,
    );
    setEditedStructure(filteredStructure);
    setChangesMade(true);
  };

  const handleWidgetResize = (
    rowIndex: number,
    widgetIndex: number,
    newColSpan: number,
  ) => {
    if (!editedStructure || stationIdx === undefined || stationIdx < 0) {
      return;
    }

    const newStructure = editedStructure.map((stationItem, sIdx) => {
      if (sIdx !== stationIdx) {
        return stationItem;
      }

      return {
        ...stationItem,
        station: {
          ...stationItem.station,
          widget_rows: stationItem.station.widget_rows.map(
            (row: any, rIdx: number) => {
              if (rIdx !== rowIndex) {
                return row;
              }

              return {
                ...row,
                widgets: row.widgets.map((widget: any, wIdx: number) => {
                  if (wIdx !== widgetIndex) {
                    return widget;
                  }

                  return {
                    ...widget,
                    colSpan: newColSpan,
                  };
                }),
              };
            },
          ),
        },
      };
    });

    setEditedStructure(newStructure);
    setChangesMade(true);
  };

  if (stationWidgetRows === null) {
    return <></>;
  }

  if (!data && !singleWidget) {
    return (
      <Card>
        <Card.Body>
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  // Single widget mode for reporting dashboards
  if (singleWidget) {
    const widgetDef = AVAILABLE_WIDGETS.find((w) => w.key === singleWidget.key);
    if (widgetDef) {
      return (
        <div className="single-widget-mode">
          {widgetDef.component?.(
            singleWidget.metaData?.sourceUUID ??
              singleWidget.metaData?.sourceUUIDs?.[0],
            singleWidget.metaData,
            singleWidget.visualSettings,
          )}
        </div>
      );
    }
    return <div>{gettext("Widget type not found")}</div>;
  }

  // Row and widget indices must refer to positions in stationWidgetRows, so
  // hidden rows and unknown widget types are skipped without re-indexing.
  // While editing nothing is skipped, so that the move arrows and drop zones
  // never have gaps and unknown widgets can be deleted.
  let visibleWidgets =
    stationWidgetRows
      ?.map((visibleWidget: any, widgetGroupIdx: number) => {
        if (visibleWidget.hidden && !canEditWidgets) {
          return null;
        }
        const widgets: WidgetGroupItem[] = visibleWidget.widgets ?? [];

        const renderItems = resolveRowWidgets(
          widgets,
          AVAILABLE_WIDGETS,
          canEditWidgets ? unknownWidgetPlaceholder : undefined,
        ).map(({ definition, widgetParams, widgetIdx }) => ({
          widget: {
            ...definition,
            sourceUUID: widgetParams.sourceUUID,
            metaData: widgetParams.metaData,
            colSpan: widgetParams.colSpan,
            visualSettings: widgetParams.visualSettings,
          } as RenderWidget,
          widgetIdx,
        }));
        const widgetRender = renderItems.map((item) => item.widget);
        if (!widgetRender.length) {
          return (
            <React.Fragment
              key={`blank_widget_${widgetGroupIdx}`}
            ></React.Fragment>
          );
        }

        return (
          <React.Fragment key={`widget_group_fragment_${widgetGroupIdx}`}>
            <RowDropZone rowIndex={widgetGroupIdx} scope={stationIdentifier} />
            <Row
              key={`widget_group_${widgetGroupIdx}`}
              className={classNames("g-3 mb-3", {
                "row-drag-mode": isDragging,
              })}
            >
              {renderItems.map(({ widget, widgetIdx }) => (
                <DraggableWidget
                  key={`draggable_widget_${widget.key}_${widgetIdx}`}
                  widget={widget}
                  scope={stationIdentifier}
                  idx={widgetIdx}
                  widgetGroupIdx={widgetGroupIdx}
                  widgetRender={widgetRender}
                  canEditWidgets={canEditWidgets}
                  isTouchDevice={isTouchDevice}
                  isMobileViewport={isMobileViewport}
                  stationWidgetRows={stationWidgetRows}
                  handleWidgetResize={handleWidgetResize}
                  showEditWidget={showEditWidget}
                  onDeleteWidget={onDeleteWidget}
                  onCopyWidget={onCopyWidget}
                  moveWidget={moveWidget}
                />
              ))}
            </Row>
          </React.Fragment>
        );
      })
      .filter((rowElement: React.ReactElement | null) => rowElement !== null) ??
    [];

  const NewWidgetButton = canEditWidgets ? (
    <Row key="add_new_widget" className="g-3 mb-3 row-add-widget">
      <Col>
        <Card>
          <Card.Body className="add-widget-button" onClick={showAddWidget}>
            <h5>+ Add widget</h5>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  ) : (
    <> </>
  );

  if (!visibleWidgets.length) {
    return (
      <>
        <Row className="g-3 mb-3">
          <Col>
            <Card>
              <Card.Body>
                <p>No widgets available</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {NewWidgetButton}
      </>
    );
  }

  return (
    <div className="station-dashboard">
      {visibleWidgets}
      <RowDropZone
        rowIndex={stationWidgetRows?.length ?? 0}
        scope={stationIdentifier}
      />
      {NewWidgetButton}
    </div>
  );
};

interface DashboardNumberProps {
  value: number;
  unit?: string | undefined;
  precision?: number;
}
const DashboardNumber = (props: DashboardNumberProps) => {
  const { value, unit, precision = 5 } = props;
  return (
    <span>
      <span style={{ fontSize: "1.76rem", fontWeight: "bold" }}>
        {formatNumberSafe(value, precision)}
      </span>
      {!!unit && (
        <span
          style={{ fontSize: "1.3rem", fontWeight: "bold", paddingLeft: "8px" }}
        >
          {unit}
        </span>
      )}
    </span>
  );
};

export default StationDashboard;
